import * as React from "react";
import PropTypes from 'prop-types';
import {reisy, use} from "reisy";
import {action} from "mobx";
import {observer} from "mobx-react";
import Arrow from "../scroller/arrow.js";
import {Position} from "../scroller/utils.js";

import Box from '../components/box.js';
import Cover from '../components/cover.js';
import Navigation from "../navigation/index.js";
import * as Icons from '../components/icons.js';
import {chooseVariant} from '../components/image.js';
import SpringSystem from '../pagehandler/math/springsystem.js';
import mapKey, {LEFT, RIGHT} from '../components/mapkey.js';

export const styles = reisy`
@namespace OldCollection;

JackInTheBox: $Settings.JackInTheBox;
Branding: $Settings.Branding;
showBack: true;
showShare: true;

container {
	@extends Helpers.Fullscreen;
	min-height: 100vh;

	overflow: hidden;
	color: #000;
	background-color: #323232;
	background-position: center;
	background-size: cover;
	background-repeat: no-repeat;
	$Media.portraitSmall {
		font-size: 12px;
	}
	$Media.landscapeSmall {
		font-size: 10px;
	}
	$Media.landscapeSmallest {
		font-size: 8px;
	}
}
Contain {
	@extends Helpers.Contain;
}
logoContainer {
	z-index: -1;
	position: absolute;
	left: calc(50% - 2.1875em / 2);
	bottom: 20px;
	height: 2.1875em;
	width: 2.1875em;
	$Media.portrait {
		display: none;
	},
	$Media.landscapeSmallest {
		display: none;
	}
}
logo {
	height: 100%;
}
cover {
	position: absolute;
	top: 0; bottom: 0;
	z-index: 5;
	margin: auto 0;
	width: 25em;
	height: 25em;
	margin-left: -18.75em;
	font-size: 1.2em;
	$Media.portrait {
		display: none;
	}
}
list {
	@extends Helpers.AbsFull;
	z-index: 0;
	overflow-y: hidden;
	overflow-x: scroll;
	touch-action: pan-x;
	-webkit-overflow-scrolling: touch;
	$Media.portrait {
		overflow-y: scroll;
		overflow-x: hidden;
		touch-action: pan-y;
	}
}
/*
 * oh how I hate that scroll containers just ignore their padding-end -_-
 * that will hopefully change with the kosk/colleciton redesign
 */
listContent {
	position: absolute;
	top: 0; bottom: 0;
	height: 26.875em;
	margin: auto 0;
	padding: 0 1em 0 8.5em;
	display: flex;
	flex-direction: row;
	$Media.portrait {
		flex-direction: column;
		height: auto;
		width: 100%;
		padding: 1em 0;
	}
}
box {
	flex-shrink: 0;
	margin: 0 3.125em;
	$Media.portrait {
		margin: 1.5625em auto;
	}
}
separator {
	width: 1px;
	height: 25em;
	background: linear-gradient(transparent 0%, #666 50%, transparent 100%);
	background-size: 1px 4px;
	flex-shrink: 0;
	$Media.portrait {
		height: 1px;
		width: 25em;
		margin: auto;
		background: linear-gradient(90deg, transparent 0%, #666 50%, transparent 100%);
		background-size: 4px 1px;
	}
}
`;

const Separator = () => (<div {...use(styles.separator)} />);

let scrollPosition;

const hideArrows = () => IS_BROWSER && window.innerWidth < window.innerHeight;

class Collection extends React.Component {
	scrollwrapper = React.createRef();
	
	constructor(props) {
		super(props);

		this.state = {
			hideArrows: hideArrows(),
		};

		this.scroller = null;
		this.setScroller = ref => (this.scroller = ref);

		this.position = new Position();

		this.layout = {};
		this.nextScroll = 0;
		this.scrollTimeout = null;

		this.spring = new SpringSystem({
			scroll: val => {
				// XXX: it may be bad for performance to write and then read this prop,
				// but we do need to rely on the browsers internal rounding here, which
				// may be different than `Math.round` when the page is zoomed on
				// desktop!
				this.scroller.scrollLeft = val;
				this.nextScroll = this.scroller.scrollLeft;
			},
		});

		this.handleResize = this.handleResize.bind(this);
		this.handleScroll = this.handleScroll.bind(this);
		this.onKeydown = this.onKeydown.bind(this);

		this.handleLeft = ev => {
			this.snapTo(this.position.target - 1);
			if (ev) { ev.preventDefault(); }
		};
		this.handleRight = ev => {
			this.snapTo(this.position.target + 1);
			if (ev) { ev.preventDefault(); }
		};
		this.componentWillReceiveProps(props);
	}

	componentDidMount() {
		if (!IS_BROWSER) { return; }
		window.addEventListener('resize', this.handleResize);
		document.addEventListener('keydown', this.onKeydown);
		this.scroller.addEventListener('scroll', this.handleScroll);
		if (scrollPosition && scrollPosition.id === this.props.collection.id) {
			this.scroller.scrollTop = scrollPosition.scroll.top;
			this.scroller.scrollLeft = scrollPosition.scroll.left;
		}
		this.componentDidUpdate();
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.handleResize);
		document.removeEventListener('keydown', this.onKeydown);
		this.scroller.removeEventListener('scroll', this.handleScroll);
		scrollPosition = {
			id: this.props.collection.id,
			scroll: {
				top: this.scroller.scrollTop,
				left: this.scroller.scrollLeft,
			},
		};
	}

	@action componentWillReceiveProps(props) {
		this.position.max = props.collection.content.length - 1;
	}

	handleResize() {
		this.setState({hideArrows: hideArrows()});
		this.componentDidUpdate();
	}

	componentDidUpdate() {
		const style = getComputedStyle(this.scrollwrapper.current);
		const paddingLeft = parseFloat(style.paddingLeft);
		const paddingRight = parseFloat(style.paddingRight);
		const wrapperWidth = Math.max(this.scrollwrapper.current.offsetWidth, this.scroller.scrollWidth);
		const width = this.scroller.offsetWidth;
		const boxWidth = (wrapperWidth - paddingLeft - paddingRight) / this.props.collection.content.length;
		const maxScroll = wrapperWidth - width;

		this.layout = {
			paddingLeft,
			paddingRight,
			boxWidth,
			width,
			wrapperWidth,
			maxScroll,
		};
	}

	render() {
		const {collection} = this.props;
		const {hideArrows} = this.state;
		const {background} = collection;
		const children = [];
		let style;
		if (background) {
			style = {backgroundSize: 'cover'};
			if (background.color) {
				style.backgroundColor = background.color;
			}
			if (background.image) {
				style.backgroundImage = `url(${chooseVariant(background.image).url})`;
			}
		}
		for (let i = 0; i < collection.content.length; i++) {
			if (i > 0) {
				children.push(<Separator key={`sep_${i}`} />);
			}
			const content = collection.content[i];
			children.push(<Box
				style={styles.box} key={content.id} content={content} />);
		}

		return (
			<section
				{...use(styles.container, styles.JackInTheBox && styles.Contain, style, collection.options && collection.options.className)}>
				<Navigation styles={styles} />
				{styles.Branding && <div {...use(styles.logoContainer)}>
					<Icons.Logo {...use(styles.logo)} />
				</div>}
				<div {...use(styles.cover)}>
					<Cover content={collection} />
				</div>
				{!hideArrows && <Arrow
					direction="start"
					onClick={this.handleLeft}
					position={this.position} />}
				<div {...use(styles.list)} ref={this.setScroller}>
					<div {...use(styles.listContent)} ref={this.scrollwrapper}>
						{children}
						<div></div> {/* empty div is for IE10, so that the margin of the last box is still part of the scrollWidth */}
					</div>
				</div>
				{!hideArrows && <Arrow
					direction="end"
					onClick={this.handleRight}
					position={this.position} />}
			</section>
		);
	}

	// triggers a snapping (spring based animation) to given `position`
	snapTo(position) {
		position = Math.max(0, Math.min(position, this.position.max));
		this.setTargetPosition(position);
		if (!this.scroller) { return; }

		const {maxScroll} = this.layout;
		const scroll = this.scroller.scrollLeft;
		const newScroll = Math.min(this.positionToScroll(position), maxScroll);
		if (newScroll !== scroll) {
			if (!this.spring.running) {
				this.spring.reset({scroll});
			}
			this.spring.set({
				scroll: newScroll,
			});
		}
		clearTimeout(this.scrollTimeout);
	}

	@action setTargetPosition(position) {
		this.position.target = position;
	}

	positionToScroll(position) {
		position = Math.max(0, Math.min(position, this.position.max));
		const {paddingRight, boxWidth} = this.layout;
		return paddingRight + boxWidth * position + 1;
	}

	scrollToPosition(scroll) {
		const {paddingRight, boxWidth} = this.layout;
		return Math.max(0, Math.floor((scroll - paddingRight) / boxWidth));
	}

	handleScroll() {
		if (this.state.hideArrows) { return; }
		const scroll = this.scroller.scrollLeft;
		const position = this.scrollToPosition(scroll);
		const {maxScroll} = this.layout;
		clearTimeout(this.scrollTimeout);
		if (scroll !== this.nextScroll) {
			this.nextScroll = -1;
			this.spring.stop(false);
			this.setTargetPosition(position);
			this.scrollTimeout = setTimeout(() => {
				if (scroll === maxScroll) {
					this.setTargetPosition(this.props.collection.content.length - 1);
				}
			}, 300);
		}
	}

	onKeydown(ev) {
		if (ev.altKey || ev.ctrlKey || ev.metaKey || ev.shiftKey) { return; }
		if (!this.scroller) { return; }
		const key = mapKey(ev);

		const isLeft = LEFT.includes(key);
		if (!isLeft && !RIGHT.includes(key)) { return; }
		ev.preventDefault();

		if (isLeft) {
			this.handleLeft();
		} else {
			this.handleRight();
		}
	}
}

Collection.propTypes = {
	collection: PropTypes.object.isRequired,
};

export default observer(Collection);



// WEBPACK FOOTER //
// ./lib/screens/oldcollection.js